// src/components/collaboration/QuickActions.jsx
import React, { useState, useEffect } from "react";
import {
  Share2, ShoppingCart, GitBranch, Megaphone, Users,
  FolderKanban, UserCheck, Loader2, X, Check, Flag,
  AlertCircle, ChevronDown,
} from "lucide-react";
import { useRunsStore }    from "../../stores/useRunsStore";
import { useLayerRegistryStore } from "../../stores/useLayerRegistryStore";
import { useAoiStore }     from "../../stores/useAoiStore";
import { saveMapView }     from "../../api/mapViewsApi";
import { useProjectStore } from "../../stores/useProjectStore";
import { useAppNav }       from "../../hooks/useAppNav";
import { fetchProjectMembers } from "../../api/collabApi";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

// ─── Action button — no longer owns open state ────────────────────────────────

function ActionButton({ icon: Icon, label, onClick, active, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cx(
        "flex items-center gap-2 px-3 py-2.5 text-[10px] font-medium",
        "rounded-xl ring-1 ring-inset ring-white/10 transition w-full",
        active
          ? "bg-sky-500/15 text-sky-200 ring-sky-500/25"
          : "bg-white/5 text-zinc-300 hover:bg-white/[0.08] hover:text-zinc-100",
        disabled && "opacity-40 cursor-not-allowed"
      )}
    >
      <Icon className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
      {label}
    </button>
  );
}

// ─── Full-width detail panel ──────────────────────────────────────────────────

function DetailPanel({ title, onClose, children }) {
  return (
    <div className="rounded-2xl bg-zinc-900 ring-1 ring-white/10 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <span className="text-[10px] font-semibold tracking-widest font-mono uppercase text-zinc-400">
          {title}
        </span>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300 transition">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="p-3 space-y-2">{children}</div>
    </div>
  );
}

// ─── Member picker ────────────────────────────────────────────────────────────

function MemberPicker({ projectId, value, onChange, placeholder = "Assign to…" }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen]       = useState(false);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    fetchProjectMembers(projectId)
      .then(({ members }) => setMembers(members || []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  const selected = value ? (members.find((m) => m.id === (value?.id ?? value)) ?? value) : null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-xl bg-zinc-950/50 ring-1 ring-white/10 text-[10px] text-zinc-300 hover:ring-white/20 transition"
      >
        <span className={selected ? "text-zinc-200" : "text-zinc-500"}>
          {loading ? "Loading…" : selected ? (selected.display_name || selected.username) : placeholder}
        </span>
        <ChevronDown className="w-3 h-3 text-zinc-500 shrink-0" />
      </button>
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 rounded-xl bg-zinc-900 ring-1 ring-white/15 overflow-hidden z-30 shadow-xl max-h-40 overflow-y-auto">
          <button
            onClick={() => { onChange(null); setOpen(false); }}
            className="w-full px-3 py-2 text-left text-[10px] text-zinc-500 hover:bg-white/5 transition"
          >
            Unassigned
          </button>
          {members.map((m) => (
            <button
              key={m.id}
              onClick={() => { onChange(m); setOpen(false); }}
              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-white/10 transition text-left"
            >
              <div className="w-5 h-5 rounded-full bg-zinc-700 flex items-center justify-center text-[9px] font-bold text-zinc-300 shrink-0">
                {(m.display_name || m.username)[0].toUpperCase()}
              </div>
              <div>
                <div className="text-[10px] text-zinc-200">{m.display_name || m.username}</div>
                <div className="text-[10px] text-zinc-500">{m.role}</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Detail panel content per action ─────────────────────────────────────────

function ShareViewDetail({ onPost, onClose }) {
  const [desc,   setDesc]   = useState("");
  const [status, setStatus] = useState(null);

  const handleShare = async () => {
    setStatus("copying");
    try {
      // Capture current map state from stores
      const layerRegistry = useLayerRegistryStore.getState();
      const layers = Object.entries(layerRegistry.layers || {})
        .filter(([, l]) => l.visible)
        .map(([id, l]) => ({ id, visible: true, opacity: l.opacity ?? 1 }));

      const activeAoiId  = useAoiStore.getState().activeAoiId ?? null;
      const projectId    = useProjectStore.getState().activeProjectId;

      // Parse viewport from URL search params
      const params   = new URLSearchParams(window.location.search);
      const zoom     = parseFloat(params.get("zoom"))  || null;
      const lat      = parseFloat(params.get("lat"))   || null;
      const lng      = parseFloat(params.get("lng"))   || null;
      const center   = lat && lng ? [lng, lat] : null;

      const context = {
        v:       1,
        map:     { center, zoom },
        layers,
        aoiId:   activeAoiId,
      };

      // Save to server — get back a short ID
      const saved = await saveMapView({
        context,
        project_id: projectId !== "global" ? projectId : null,
        label:      desc.trim() || "Shared map view",
      });

      // Build share URL with view ID
      const baseUrl = window.location.origin + window.location.pathname;
      const shareUrl = `${baseUrl}?view=${saved.id}`;

      await navigator.clipboard.writeText(shareUrl);

      const body = desc.trim() || "Shared map view";
      await onPost({
        type: "link",
        body,
        metadata: {
          ref_type:  "view",
          ref_label: "Shared map view",
          ref_url:   shareUrl,
          view_id:   saved.id,
        },
      });

      setStatus("done");
      setTimeout(() => { setStatus(null); onClose(); }, 1500);
    } catch (err) {
      console.error("[ShareView] failed", err);
      setStatus("error");
      setTimeout(() => setStatus(null), 2000);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleShare(); }
  };

  return (
    <DetailPanel title="Share view" onClose={onClose}>
      {/* Description */}
      <div>
        <label className="text-[9px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">
          Why are you sharing this? <span className="normal-case text-zinc-600">(optional)</span>
        </label>
        <textarea
          autoFocus
          rows={2}
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="e.g. Look at this AOI boundary issue…"
          className="w-full resize-none bg-zinc-950/50 rounded-xl ring-1 ring-white/10 px-3 py-2 text-[10px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none leading-relaxed"
        />
      </div>

      <button
        onClick={handleShare}
        disabled={status === "copying"}
        className={cx(
          "w-full flex items-center justify-center gap-2 py-2 rounded-xl text-[10px] font-medium ring-1 ring-inset transition",
          status === "done"
            ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/25"
            : status === "error"
            ? "bg-red-500/15 text-red-300 ring-red-500/25"
            : "bg-sky-500/15 text-sky-200 ring-sky-500/25 hover:bg-sky-500/20"
        )}
      >
        {status === "done"    ? <><Check className="w-3.5 h-3.5" /> Copied!</> :
         status === "error"   ? <><AlertCircle className="w-3.5 h-3.5" /> Failed</> :
         status === "copying" ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Copying…</> :
                                <><Share2 className="w-3.5 h-3.5" /> Copy & post link</>}
      </button>
    </DetailPanel>
  );
}

const SEVERITIES      = ["low", "medium", "high"];
const FLAG_CATEGORIES = ["Detection error", "Missing data", "Imagery quality", "AOI boundary", "Other"];

function FlagIssueDetail({ onPost, onClose }) {
  const [severity,   setSeverity]   = useState("medium");
  const [category,   setCategory]   = useState("");
  const [desc,       setDesc]       = useState("");
  const [assignee,   setAssignee]   = useState(null);
  const [aoiId,      setAoiId]      = useState(null);
  const [sending,    setSending]    = useState(false);

  const projectId = useProjectStore((s) => s.activeProject?.id ?? null);
  const aois      = useAoiStore((s) => s.aois);

  const handleSubmit = async () => {
    if (!desc.trim()) return;
    setSending(true);
    try {
      const handle = assignee?.username ? `@${assignee.username} ` : "";
      const body   = `${handle}${desc.trim()}`;
      await onPost({
        type: "flag",
        body,
        metadata: {
          severity,
          category:          category        || null,
          aoi_id:            aoiId           || null,
          assignee_id:       assignee?.id    || null,
          assignee_username: assignee?.username || null,
          status: "open",
        },
      });
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <DetailPanel title="Flag issue" onClose={onClose}>
      {/* Severity */}
      <div>
        <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">Severity</label>
        <div className="flex gap-1.5">
          {SEVERITIES.map((s) => (
            <button key={s} onClick={() => setSeverity(s)}
              className={cx("flex-1 py-1.5 rounded-lg text-[10px] font-medium ring-1 ring-inset transition capitalize",
                severity === s
                  ? s === "high"   ? "bg-red-500/20 text-red-300 ring-red-500/30"
                    : s === "medium" ? "bg-yellow-500/20 text-yellow-300 ring-yellow-500/30"
                    : "bg-zinc-500/20 text-zinc-300 ring-white/20"
                  : "bg-transparent text-zinc-500 ring-white/10 hover:text-zinc-300"
              )}>{s}</button>
          ))}
        </div>
      </div>

      {/* Category */}
      <div>
        <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">Category</label>
        <div className="grid grid-cols-2 gap-1">
          {FLAG_CATEGORIES.map((c) => (
            <button key={c} onClick={() => setCategory(category === c ? "" : c)}
              className={cx("px-2 py-1.5 rounded-lg text-[10px] ring-1 ring-inset transition text-left",
                category === c
                  ? "bg-white/10 text-zinc-100 ring-white/20"
                  : "bg-transparent text-zinc-500 ring-white/10 hover:text-zinc-300"
              )}>{c}</button>
          ))}
        </div>
      </div>

      {/* AOI */}
      {aois.length > 0 && (
        <div>
          <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">Affected AOI</label>
          <select value={aoiId || ""} onChange={(e) => setAoiId(e.target.value || null)}
            className="w-full bg-zinc-950/50 ring-1 ring-white/10 rounded-xl px-3 py-2 text-[10px] text-zinc-300 focus:outline-none">
            <option value="">None</option>
            {aois.map((a) => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
          </select>
        </div>
      )}

      {/* Assignee */}
      {projectId && (
        <div>
          <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">Assign to</label>
          <MemberPicker projectId={projectId} value={assignee} onChange={setAssignee} />
        </div>
      )}

      {/* Description */}
      <div>
        <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">Description</label>
        <textarea autoFocus rows={2} value={desc} onChange={(e) => setDesc(e.target.value)}
          placeholder="Describe the issue…"
          className="w-full resize-none bg-zinc-950/50 rounded-xl ring-1 ring-white/10 px-3 py-2 text-[10px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none leading-relaxed" />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="px-3 py-1 text-[10px] text-zinc-500 hover:text-zinc-300 transition">Cancel</button>
        <button onClick={handleSubmit} disabled={!desc.trim() || sending}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-red-500/15 text-red-300 text-[10px] font-medium ring-1 ring-inset ring-red-500/25 hover:bg-red-500/20 disabled:opacity-40 transition">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Flag className="w-3 h-3" />}
          {sending ? "Posting…" : "Post flag"}
        </button>
      </div>
    </DetailPanel>
  );
}

function LinkRunDetail({ onPost, onClose }) {
  const runs     = useRunsStore((s) => s.runs);
  const loading  = useRunsStore((s) => s.loading);
  const loadRuns = useRunsStore((s) => s.loadRuns);

  useEffect(() => { if (runs.length === 0) loadRuns(); }, []);

  const handleSelect = async (run) => {
    await onPost({
      type: "link", body: "",
      metadata: { ref_type: "run", ref_id: run.id, ref_label: `${run.task_key} · ${run.run_type}`, ref_ts: run.created_at },
    });
    onClose();
  };

  return (
    <DetailPanel title="Link a run" onClose={onClose}>
      {loading && <div className="flex items-center gap-2 text-[10px] text-zinc-500"><Loader2 className="w-3 h-3 animate-spin" /> Loading runs…</div>}
      {!loading && runs.length === 0 && <p className="text-[10px] text-zinc-500">No runs for this project.</p>}
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {runs.slice(0, 10).map((run) => (
          <button key={run.id} onClick={() => handleSelect(run)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg hover:bg-white/10 transition text-left">
            <div>
              <div className="text-[10px] text-zinc-200 font-medium">{run.task_key}</div>
              <div className="text-[10px] text-zinc-500">{run.run_type} · {new Date(run.created_at).toLocaleDateString()}</div>
            </div>
            <GitBranch className="w-3 h-3 text-zinc-600 shrink-0" />
          </button>
        ))}
      </div>
    </DetailPanel>
  );
}

function LinkProjectDetail({ onPost, onClose }) {
  const [selected, setSelected] = useState(null);
  const [note,     setNote]     = useState("");
  const [sending,  setSending]  = useState(false);
  const projects = useProjectStore((s) => s.projects);

  const handleSubmit = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await onPost({
        type: "link",
        body: note.trim() || "",
        metadata: { ref_type: "project", ref_id: selected.id, ref_label: selected.name, note: note.trim() || null },
      });
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <DetailPanel title="Link a project" onClose={onClose}>
      <div>
        <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">Project</label>
        <div className="space-y-1 max-h-36 overflow-y-auto">
          {projects.length === 0 && <p className="text-[10px] text-zinc-500">No projects available.</p>}
          {projects.map((p) => (
            <button key={p.id} onClick={() => setSelected(selected?.id === p.id ? null : p)}
              className={cx("w-full flex items-center justify-between px-2.5 py-2 rounded-lg transition text-left",
                selected?.id === p.id ? "bg-sky-500/15 ring-1 ring-sky-500/25 text-sky-200" : "hover:bg-white/10 text-zinc-300")}>
              <span className="text-[10px]">{p.name}</span>
              {selected?.id === p.id && <Check className="w-3 h-3 shrink-0" />}
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div>
          <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">
            Note <span className="normal-case text-zinc-600">(optional)</span>
          </label>
          <textarea autoFocus rows={2} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder={`Add context about ${selected.name}…`}
            className="w-full resize-none bg-zinc-950/50 rounded-xl ring-1 ring-white/10 px-3 py-2 text-[10px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none leading-relaxed" />
        </div>
      )}

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="px-3 py-1 text-[10px] text-zinc-500 hover:text-zinc-300 transition">Cancel</button>
        <button onClick={handleSubmit} disabled={!selected || sending}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-sky-500/15 text-sky-200 text-[10px] font-medium ring-1 ring-inset ring-sky-500/25 hover:bg-sky-500/20 disabled:opacity-40 transition">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <FolderKanban className="w-3 h-3" />}
          {sending ? "Linking…" : "Link project"}
        </button>
      </div>
    </DetailPanel>
  );
}

const PRIORITIES = ["low", "medium", "high", "urgent"];

function RequestReviewDetail({ onPost, onClose }) {
  const [note,       setNote]       = useState("");
  const [priority,   setPriority]   = useState("medium");
  const [assignee, setAssignee] = useState(null);
  const [sending,    setSending]    = useState(false);

  const projectId   = useProjectStore((s) => s.activeProject?.id   ?? null);
  const projectName = useProjectStore((s) => s.activeProject?.name ?? null);

  const handleSubmit = async () => {
    setSending(true);
    try {
      const handle = assignee?.username ? `@${assignee.username} ` : "";
      const body   = `${handle}${note.trim() || "Review requested."}`;
      await onPost({
        type: "flag",
        body,
        metadata: {
          severity: priority,
          status: "open",
          action: "review_request",
          assignee_id:       assignee?.id       || null,
          assignee_username: assignee?.username  || null,
          project_id:   projectId,
          project_name: projectName,
        },
      });
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <DetailPanel title="Request review" onClose={onClose}>
      {/* Priority */}
      <div>
        <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">Priority</label>
        <div className="flex gap-1.5">
          {PRIORITIES.map((p) => (
            <button key={p} onClick={() => setPriority(p)}
              className={cx("flex-1 py-1.5 rounded-lg text-[10px] font-medium ring-1 ring-inset transition capitalize",
                priority === p
                  ? p === "urgent" ? "bg-red-500/20 text-red-300 ring-red-500/30"
                    : p === "high"   ? "bg-orange-500/20 text-orange-300 ring-orange-500/30"
                    : p === "medium" ? "bg-yellow-500/20 text-yellow-300 ring-yellow-500/30"
                    : "bg-zinc-500/20 text-zinc-300 ring-white/20"
                  : "bg-transparent text-zinc-500 ring-white/10 hover:text-zinc-300"
              )}>{p}</button>
          ))}
        </div>
      </div>

      {/* Assignee */}
      {projectId && (
        <div>
          <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">Assign to</label>
          <MemberPicker projectId={projectId} value={assignee} onChange={setAssignee} />
        </div>
      )}

      {/* Project context */}
      {projectName && (
        <div className="px-2 py-1.5 rounded-lg bg-white/5 ring-1 ring-white/10">
          <span className="text-[10px] text-zinc-500">Project: </span>
          <span className="text-[10px] text-zinc-300">{projectName}</span>
        </div>
      )}

      {/* Note */}
      <div>
        <label className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest mb-1 block">
          Note <span className="normal-case text-zinc-600">(optional)</span>
        </label>
        <textarea autoFocus rows={2} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="What needs to be reviewed?"
          className="w-full resize-none bg-zinc-950/50 rounded-xl ring-1 ring-white/10 px-3 py-2 text-[10px] text-zinc-200 placeholder:text-zinc-600 focus:outline-none leading-relaxed" />
      </div>

      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onClose} className="px-3 py-1 text-[10px] text-zinc-500 hover:text-zinc-300 transition">Cancel</button>
        <button onClick={handleSubmit} disabled={sending}
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-sky-500/15 text-sky-200 text-[10px] font-medium ring-1 ring-inset ring-sky-500/25 hover:bg-sky-500/20 disabled:opacity-40 transition">
          {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
          {sending ? "Sending…" : "Send request"}
        </button>
      </div>
    </DetailPanel>
  );
}

// ─── Action configs ───────────────────────────────────────────────────────────

const PROJECT_ACTIONS = [
  { id: "share",   icon: Share2,        label: "Share view",      Detail: ShareViewDetail },
  { id: "flag",    icon: Flag,          label: "Flag issue",      Detail: FlagIssueDetail },
  { id: "imagery", icon: ShoppingCart,  label: "Request imagery", Detail: null },
  { id: "run",     icon: GitBranch,     label: "Link run",        Detail: LinkRunDetail },
];

const ORG_ACTIONS = [
  { id: "announce", icon: Megaphone,    label: "Announce",        Detail: null },
  { id: "mention",  icon: Users,        label: "Mention team",    Detail: null },
  { id: "project",  icon: FolderKanban,label: "Link project",     Detail: LinkProjectDetail },
  { id: "review",   icon: UserCheck,    label: "Request review",  Detail: RequestReviewDetail },
];

// ─── Main export ──────────────────────────────────────────────────────────────

export default function QuickActions({ scope, contextKey, onPost, onOpenComposer }) {
  const [activeId, setActiveId] = useState(null);
  const { goMapTab } = useAppNav();

  const actions = scope === "project" ? PROJECT_ACTIONS : ORG_ACTIONS;
  const active  = actions.find((a) => a.id === activeId);

  const handleClick = (action) => {
    // Actions with no Detail panel have immediate side effects
    if (!action.Detail) {
      if (action.id === "imagery") {
        onPost({ type: "system", body: "Imagery request initiated.", metadata: { action: "imagery_request" } }).catch((e) => { console.error("[ui/src/components/collaboration/QuickActions.jsx] collab post: imagery request failed (swallowed):", e); });
        goMapTab("purchase");
        return;
      }
      if (action.id === "announce") { onOpenComposer("📢 Announcement: "); return; }
      if (action.id === "mention")  { onOpenComposer("@"); return; }
      return;
    }
    // Toggle detail panel
    setActiveId((cur) => (cur === action.id ? null : action.id));
  };

  return (
    <div className="space-y-2">
      <p className="text-[10px] font-semibold tracking-widest font-mono uppercase text-zinc-500">
        Quick actions
      </p>

      {/* 2-col button grid — always fixed height, never stretched by panel */}
      <div className="grid grid-cols-2 gap-2">
        {actions.map((action) => (
          <ActionButton
            key={action.id}
            icon={action.icon}
            label={action.label}
            active={activeId === action.id}
            onClick={() => handleClick(action)}
          />
        ))}
      </div>

      {/* Full-width detail panel — outside the grid */}
      {active?.Detail && (
        <active.Detail
          onPost={onPost}
          onClose={() => setActiveId(null)}
        />
      )}
    </div>
  );
}

